import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, UnlockModal } from "@/components/app/Achievements";
import MotivationCard from "@/components/app/MotivationCard";
import VisionCountdownStrip from "@/components/app/VisionCountdownStrip";
import {
  Loader2, Flame, Check, Sparkles, MessageSquare, TrendingUp, X,
  Sun, Target, ArrowRight, PartyPopper, Medal, Trophy, Crown, BookOpen,
} from "lucide-react";

const COACH_AVATAR = "https://images.pexels.com/photos/9064790/pexels-photo-9064790.jpeg?auto=compress&cs=tinysrgb&w=200";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/* Animated circular Life Score ring */
function LifeScoreRing({ score }) {
  const size = 132, stroke = 14, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const color = score >= 80 ? "#06D6A0" : score >= 50 ? "#FF7E67" : "#FFB347";
  const label = score >= 80 ? "Thriving!" : score >= 50 ? "On a roll" : "Let's build";
  return (
    <div className="relative flex flex-col items-center" data-testid="life-score-ring">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <defs>
            <linearGradient id="score-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FF7E67" />
              <stop offset="100%" stopColor="#FFB347" />
            </linearGradient>
          </defs>
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#FFE8E3" strokeWidth={stroke} fill="none" />
          <motion.circle
            cx={size / 2} cy={size / 2} r={r} stroke={score >= 80 ? "#06D6A0" : "url(#score-gradient)"}
            strokeWidth={stroke} fill="none" strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: c - c * pct }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            className="font-nunito text-4xl font-extrabold text-[#2D3748]"
            initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.5 }}
          >{score}</motion.span>
          <span className="font-quicksand text-xs font-semibold text-[#718096]">of 100</span>
        </div>
      </div>
      <span className="mt-3 rounded-full px-3 py-1 font-nunito text-sm font-bold" style={{ background: color + "22", color }}>{label}</span>
    </div>
  );
}

/* Small celebratory particle burst */
function Burst({ show }) {
  if (!show) return null;
  const parts = Array.from({ length: 8 });
  const colors = ["#FF7E67", "#FFB347", "#FFD166", "#06D6A0"];
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      {parts.map((_, i) => {
        const a = (i / parts.length) * Math.PI * 2;
        return (
          <motion.span key={i} className="absolute h-2 w-2 rounded-full"
            style={{ background: colors[i % colors.length] }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x: Math.cos(a) * 34, y: Math.sin(a) * 34, opacity: 0, scale: 0.4 }}
            transition={{ duration: 0.6, ease: "easeOut" }} />
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [gen, setGen] = useState(false);
  const [checkin, setCheckin] = useState(false);
  const [celebrate, setCelebrate] = useState(null); // id of item being celebrated
  const [badges, setBadges] = useState(null);
  const [unlocked, setUnlocked] = useState(null);
  const [challenges, setChallenges] = useState([]);

  const load = useCallback(async () => {
    const [today, life, ach, ch] = await Promise.all([
      api.get("/daily-plan/today"), api.get("/life-score"), api.get("/achievements"),
      api.get("/challenges/summary"),
    ]);
    setData({ ...today.data, life: life.data });
    setBadges(ach.data);
    setChallenges(ch.data.challenges);
  }, []);

  useEffect(() => { load(); }, [load]);

  const generatePlan = async () => {
    setGen(true);
    try {
      await api.post("/daily-plan/generate");
      await load();
      toast.success("Today's plan is ready!");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const toggleHabit = async (h) => {
    if (h.done_today) return;
    setCelebrate(`habit-${h.id}`);
    setTimeout(() => setCelebrate(null), 650);
    const res = await api.post(`/habits/${h.id}/log`);
    await load();
    toast.success(`${h.name} — streak up! Keep it burning.`);
    if (res.data?.unlocked?.length) setUnlocked(res.data.unlocked);
  };

  const completeTask = async (t) => {
    setCelebrate(`task-${t.id}`);
    setTimeout(() => setCelebrate(null), 650);
    const res = await api.post(`/tasks/${t.id}/complete`);
    await load();
    if (res.data?.unlocked?.length) setUnlocked(res.data.unlocked);
  };

  if (!data) return (
    <div className="flex flex-col items-center justify-center py-32 text-[#718096]">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" />
      <p className="mt-3 font-quicksand text-sm">Warming things up…</p>
    </div>
  );

  const plan = data.plan?.plan;
  const overall = data.life?.overall ?? 0;
  const firstName = user?.name?.split(" ")[0] || "there";

  const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

  return (
    <div className="font-quicksand" data-testid="dashboard">
      {/* Greeting header */}
      <motion.div {...fade} transition={{ duration: 0.5 }}
        className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-quicksand text-sm font-semibold text-[#FF7E67]">
            <Sun className="h-4 w-4" strokeWidth={2.5} />
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </p>
          <h1 className="mt-1 font-nunito text-4xl font-extrabold tracking-tight text-[#2D3748] sm:text-5xl">
            {greeting()}, <span className="text-sunrise">{firstName}!</span>
          </h1>
          <p className="mt-2 font-quicksand text-base font-medium text-[#718096]">Here&rsquo;s your world today — let&rsquo;s make it count.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setCheckin(true)} data-testid="checkin-btn"
            className="flex items-center gap-2 rounded-full border border-[#FFE8E3] bg-white px-5 py-3 font-nunito text-sm font-bold text-[#FF7E67] transition-colors duration-200 hover:bg-[#FFE8E3]">
            <TrendingUp className="h-4 w-4" strokeWidth={2.5} /> Check-in
          </button>
          <button onClick={() => navigate("/app/coach")} data-testid="coach-quick-btn"
            className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-105">
            <MessageSquare className="h-4 w-4" strokeWidth={2.5} /> My Coach
          </button>
        </div>
      </motion.div>

      <MotivationCard />

      <VisionCountdownStrip />

      {/* Top bento */}
      <div className="mb-8 grid gap-8 md:grid-cols-3">
        {/* Life score */}
        <motion.div {...fade} transition={{ duration: 0.5, delay: 0.05 }}
          className="flex flex-col items-center justify-center rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
          <h2 className="mb-4 font-nunito text-lg font-bold text-[#2D3748]">Life Score</h2>
          <LifeScoreRing score={overall} />
        </motion.div>

        {/* Today's focus */}
        <motion.div {...fade} transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm md:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF4E6]">
                <Sparkles className="h-5 w-5 text-[#FFB347]" strokeWidth={2.5} />
              </span>
              Today&rsquo;s Focus
            </h2>
            <button onClick={generatePlan} disabled={gen} data-testid="generate-plan-btn"
              className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-105 disabled:opacity-70">
              {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Generate Plan <ArrowRight className="h-4 w-4" strokeWidth={2.5} /></>}
            </button>
          </div>
          {plan ? (
            <div className="mt-6">
              <p className="rounded-2xl bg-[#FFF4E6] px-5 py-4 font-quicksand text-base font-medium text-[#2D3748]">{plan.motivation}</p>
              <ul className="mt-5 space-y-3">
                {(plan.top_priorities || []).map((p, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
                    className="flex items-start gap-3 font-quicksand text-base text-[#4A5568]">
                    <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FFE8E3] font-nunito text-xs font-bold text-[#FF7E67]">{i + 1}</span>
                    {p}
                  </motion.li>
                ))}
              </ul>
              {plan.focus_tip && <p className="mt-5 flex items-start gap-2 font-quicksand text-sm font-medium text-[#718096]"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#FFB347]" strokeWidth={2.5} />{plan.focus_tip}</p>}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#FFE8E3] py-10 text-center">
              <Sparkles className="h-8 w-8 text-[#FFB347]" strokeWidth={2} />
              <p className="mt-3 font-nunito text-base font-bold text-[#2D3748]">Your day is a blank canvas!</p>
              <p className="font-quicksand text-sm text-[#718096]">Tap Generate Plan for your AI-crafted priorities.</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Bottom bento */}
      <div className="grid gap-8 md:grid-cols-3">
        {/* Habits */}
        <motion.div {...fade} transition={{ duration: 0.5, delay: 0.15 }}
          className="rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm md:col-span-2">
          <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
            <Flame className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Habits
          </h2>
          <div className="mt-5 space-y-3">
            {data.habits.length === 0 && (
              <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-[#FFE8E3] py-8 text-center">
                <Target className="h-7 w-7 text-[#FFB347]" strokeWidth={2} />
                <p className="mt-2 font-quicksand text-sm text-[#718096]">Habits will appear here from your goals.</p>
              </div>
            )}
            {data.habits.map((h) => (
              <button key={h.id} onClick={() => toggleHabit(h)} data-testid={`habit-${h.id}`}
                className="group relative flex w-full items-center justify-between rounded-2xl border border-transparent bg-[#FFF9F5] p-4 text-left transition-colors duration-200 hover:border-[#FFE8E3] hover:bg-white">
                <Burst show={celebrate === `habit-${h.id}`} />
                <span className="flex items-center gap-3">
                  <motion.span whileTap={{ scale: 0.8 }}
                    className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors duration-200 ${h.done_today ? "border-[#06D6A0] bg-[#06D6A0]" : "border-[#FFD6CC] bg-white group-hover:border-[#FF7E67]"}`}>
                    {h.done_today && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                  </motion.span>
                  <span className={`font-quicksand text-base font-semibold ${h.done_today ? "text-[#718096] line-through" : "text-[#2D3748]"}`}>{h.name}</span>
                </span>
                <span className="flex items-center gap-1 rounded-full bg-[#FFF4E6] px-3 py-1 font-nunito text-sm font-bold text-[#FFB347]">
                  <Flame className="h-4 w-4" strokeWidth={2.5} />{h.streak}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tasks */}
        <motion.div {...fade} transition={{ duration: 0.5, delay: 0.2 }}
          className="rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
          <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
            <Check className="h-5 w-5 text-[#06D6A0]" strokeWidth={2.5} /> Tasks
          </h2>
          <div className="mt-5 space-y-3">
            {data.tasks.length === 0 && (
              <div className="flex flex-col items-center rounded-2xl border-2 border-dashed border-[#E6FBF5] py-8 text-center">
                <PartyPopper className="h-7 w-7 text-[#06D6A0]" strokeWidth={2} />
                <p className="mt-2 font-quicksand text-sm text-[#718096]">All clear. Nice work!</p>
              </div>
            )}
            {data.tasks.slice(0, 8).map((t) => (
              <button key={t.id} onClick={() => completeTask(t)} data-testid={`task-${t.id}`}
                className="group relative flex w-full items-center gap-3 rounded-2xl border border-transparent bg-[#FFF9F5] p-4 text-left transition-colors duration-200 hover:border-[#FFE8E3] hover:bg-white">
                <Burst show={celebrate === `task-${t.id}`} />
                <motion.span whileTap={{ scale: 0.8 }}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-[#CDEFE4] bg-white transition-colors duration-200 group-hover:border-[#06D6A0]">
                  <Check className="h-3.5 w-3.5 text-transparent transition-colors duration-200 group-hover:text-[#06D6A0]" strokeWidth={3} />
                </motion.span>
                <span className="font-quicksand text-sm font-semibold text-[#2D3748]">{t.title}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Coach nudge */}
      <motion.button {...fade} transition={{ duration: 0.5, delay: 0.25 }}
        onClick={() => navigate("/app/coach")} data-testid="coach-banner-btn"
        className="mt-8 flex w-full items-center justify-between rounded-[2rem] bg-sunrise p-6 text-left shadow-glow-coral transition-transform duration-200 hover:scale-[1.01]">
        <div className="flex items-center gap-4">
          <img src={COACH_AVATAR} alt="Your AI coach" className="h-14 w-14 rounded-full border-2 border-white/70 object-cover" />
          <div>
            <p className="font-nunito text-lg font-extrabold text-white">Talk it through with your coach</p>
            <p className="font-quicksand text-sm font-medium text-white/85">Stuck, celebrating, or planning — I&rsquo;m here for you.</p>
          </div>
        </div>
        <span className="hidden h-11 w-11 items-center justify-center rounded-full bg-white/25 sm:flex">
          <ArrowRight className="h-5 w-5 text-white" strokeWidth={2.5} />
        </span>
      </motion.button>

      {/* Your Challenges */}
      {challenges.length > 0 && (
        <motion.div {...fade} transition={{ duration: 0.5, delay: 0.28 }}
          className="mt-8 rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
              <Trophy className="h-5 w-5 text-[#FFB347]" strokeWidth={2.5} /> Your Challenges
            </h2>
            <button onClick={() => navigate("/app/community")} data-testid="view-challenges-btn"
              className="flex items-center gap-1 font-nunito text-sm font-bold text-[#FF7E67] hover:underline">
              View all <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {challenges.map((c) => {
              const pct = Math.min(100, Math.round((c.my_progress / c.target) * 100));
              return (
                <button key={c.id} onClick={() => navigate("/app/community")} data-testid={`dash-challenge-${c.id}`}
                  className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-4 text-left transition-colors hover:bg-white">
                  <div className="flex items-center justify-between">
                    <p className="font-nunito text-sm font-bold text-[#2D3748]">{c.title}</p>
                    {c.my_rank && (
                      <span className="flex items-center gap-1 rounded-full bg-[#FFF4E6] px-2 py-0.5 font-nunito text-xs font-bold text-[#B45309]">
                        <Crown className="h-3.5 w-3.5" strokeWidth={2.5} /> #{c.my_rank}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
                    <div className="h-full rounded-full bg-sunrise" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-1.5 font-quicksand text-xs font-semibold text-[#718096]">{c.my_progress}/{c.target} {c.metric_label} · {c.participant_count} in · {c.days_left}d left</p>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Badges */}
      <motion.div {...fade} transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-8 rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
            <Medal className="h-5 w-5 text-[#FFB347]" strokeWidth={2.5} /> Your Badges
          </h2>
          {badges && (
            <span className="rounded-full bg-[#FFF4E6] px-3 py-1 font-nunito text-sm font-bold text-[#B45309]" data-testid="badge-count">
              {badges.unlocked_count} / {badges.total}
            </span>
          )}
        </div>
        {badges ? (
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="badge-grid">
            {badges.achievements.map((a) => <Badge key={a.id} a={a} showShare />)}
          </div>
        ) : (
          <div className="mt-5 flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" /></div>
        )}
      </motion.div>

      <AnimatePresence>{checkin && <CheckinModal onClose={() => setCheckin(false)} onResult={(u) => u?.length && setUnlocked(u)} />}</AnimatePresence>
      {unlocked && <UnlockModal badges={unlocked} onClose={() => setUnlocked(null)} />}
    </div>
  );
}

const MOOD_CHIPS = [
  { id: "great", emoji: "😄", label: "Great" },
  { id: "good", emoji: "🙂", label: "Good" },
  { id: "okay", emoji: "😐", label: "Okay" },
  { id: "low", emoji: "😕", label: "Low" },
  { id: "rough", emoji: "😔", label: "Rough" },
];

function CheckinModal({ onClose, onResult }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState({ feeling: "", completed: "", missed: "", obstacles: "", confidence: 7 });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [journalId, setJournalId] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [reflecting, setReflecting] = useState(false);

  const canReflect = user?.plan && user.plan !== "free";

  const submit = async () => {
    if (!f.feeling) { toast.error("Pick your mood first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/daily-checkins", f);
      setRes(data.crisis ? { crisis: data.message } : data.result);
      setJournalId(data.journal_entry_id || null);
      if (data.unlocked?.length) onResult?.(data.unlocked);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const reflectNow = async () => {
    if (!journalId) return;
    setReflecting(true);
    try {
      const { data } = await api.post(`/journal/${journalId}/reflect`);
      setReflection(data.ai_reflection);
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setReflecting(false); }
  };

  const openJournal = () => { onClose(); navigate("/app/journal"); };

  const inputCls = "h-12 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-[#2D3748] outline-none transition-colors focus:border-[#FF7E67] focus:bg-white";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[2rem] bg-white p-8 shadow-warm-lg" onClick={(e) => e.stopPropagation()} data-testid="checkin-modal">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-nunito text-2xl font-extrabold text-[#2D3748]">
            <TrendingUp className="h-6 w-6 text-[#FF7E67]" strokeWidth={2.5} /> Daily Check-in
          </h3>
          <button onClick={onClose} data-testid="close-checkin-btn" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF9F5] text-[#718096] transition-colors hover:bg-[#FFE8E3]"><X className="h-5 w-5" /></button>
        </div>
        {res ? (
          <div className="mt-6 space-y-3 font-quicksand text-base">
            {res.crisis ? <p className="rounded-2xl border border-[#FF7E67]/30 bg-[#FFE8E3] p-4 text-[#2D3748]">{res.crisis}</p> : (
              <>
                <p className="text-[#2D3748]">{res.encouragement}</p>
                <p className="rounded-2xl bg-[#E6FBF5] p-4 text-[#2D3748]"><b className="text-[#06D6A0]">Next action:</b> {res.next_action}</p>
                {res.risk_alerts?.length > 0 && <p className="text-[#718096]">Watch: {res.risk_alerts.join(", ")}</p>}

                {/* Journal auto-link + one-tap reflect */}
                {journalId && !res.crisis && (
                  <div className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-4" data-testid="checkin-journal-link">
                    <div className="mb-2 flex items-center gap-2 text-[#C2410C]">
                      <BookOpen className="h-4 w-4" strokeWidth={2.5} />
                      <span className="font-nunito text-xs font-bold uppercase tracking-wider">Saved to your journal</span>
                    </div>
                    {reflection ? (
                      <>
                        <div className="mb-1 flex items-center gap-2 text-[#C2410C]">
                          <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                          <span className="font-nunito text-xs font-bold uppercase tracking-wider">Reflection</span>
                        </div>
                        <p className="whitespace-pre-wrap font-quicksand text-sm text-[#2D3748]">{reflection}</p>
                      </>
                    ) : (
                      <button onClick={reflectNow} disabled={reflecting} data-testid="checkin-reflect-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-[#FFE8E3] px-4 py-2 font-nunito text-xs font-bold text-[#C2410C] hover:bg-[#FFD4C7]">
                        {reflecting
                          ? <><Loader2 className="h-3 w-3 animate-spin" /> Reflecting…</>
                          : canReflect
                            ? <><Sparkles className="h-3 w-3" strokeWidth={2.5} /> Reflect on this with AI</>
                            : <><Sparkles className="h-3 w-3" strokeWidth={2.5} /> Reflect with AI · Plus</>}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="flex gap-3 pt-1">
              {journalId && !res.crisis && (
                <button onClick={openJournal} data-testid="checkin-open-journal-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#FFE8E3] py-3 font-nunito font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
                  <BookOpen className="h-4 w-4" /> Open Journal
                </button>
              )}
              <button onClick={onClose} className="flex-1 rounded-full bg-sunrise py-3 font-nunito font-bold text-white shadow-glow-coral">Done</button>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div>
              <p className="mb-2 font-quicksand text-sm font-semibold text-[#4A5568]">How are you feeling today?</p>
              <div className="flex flex-wrap gap-2" data-testid="checkin-mood-picker">
                {MOOD_CHIPS.map((m) => (
                  <button key={m.id} type="button" onClick={() => setF({ ...f, feeling: m.id })}
                    data-testid={`checkin-mood-${m.id}`}
                    className={`rounded-full border px-3.5 py-1.5 font-nunito text-xs font-bold transition-all ${
                      f.feeling === m.id
                        ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C] scale-105"
                        : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"
                    }`}>
                    <span className="mr-1">{m.emoji}</span>{m.label}
                  </button>
                ))}
              </div>
            </div>
            <input placeholder="What did you complete?" value={f.completed} onChange={(e) => setF({ ...f, completed: e.target.value })} className={inputCls} data-testid="checkin-completed" />
            <input placeholder="What got in your way?" value={f.obstacles} onChange={(e) => setF({ ...f, obstacles: e.target.value })} className={inputCls} data-testid="checkin-obstacles" />
            <label className="block font-quicksand text-sm font-semibold text-[#4A5568]">Confidence for tomorrow: <span className="text-[#FF7E67]">{f.confidence}/10</span>
              <input type="range" min="1" max="10" value={f.confidence} onChange={(e) => setF({ ...f, confidence: Number(e.target.value) })} className="mt-2 w-full accent-[#FF7E67]" />
            </label>
            <button onClick={submit} disabled={loading} data-testid="submit-checkin-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02] disabled:opacity-70">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Check-in"}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
